// C:\Users\cornn\source\repos\purehealthnexus\src\components\AiDashboard\AiMyNutritionStatus\AiUser\AiUserSettings\AiUserSettings.jsx
import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  Button,
  Input,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../../../nutrition/nutrition-ui-kit";

import { useAuth } from "../../../../../firebase/AuthContext";
import { db } from "../../../../../firebase/firebaseConfig";
import { doc, setDoc, deleteField } from "firebase/firestore";

import "./AiUserSettings.css";

// All constants + helpers in separate file (no magic literals in JSX)
import {
  sexOptions,
  physicalActivityOptions,
  advancedActivityTypeOptions,
  mealPrefDietStyleOptions,
  mealPrefAllergyOptions,
  mealPrefReligiousOptions,
  mealPrefDislikeOptions,
  mealPrefConvenienceOptions,
  FIELD_LABELS,
  BUTTON_LABELS,
  VALIDATION_MESSAGES,
  UI_COPY,
  ERROR_MESSAGES,
  ACTIVITY_LEVEL_MAP,
  FIRESTORE_COLLECTIONS,
  HEIGHT_UNITS,
  WEIGHT_UNITS,
  ADVANCED_ACTIVITY_NONE_ID,
  convertFeetAndInchesToCentimeters,
  convertPoundsToKilograms,
  centimetersToFeetInches,
  kilogramsToPounds,
} from "./AiUserSettingConst";

export default function AiUserSettings({
  open,
  onClose,
  initialProfile,
  onSaveProfile,
}) {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  // Derive a full editable state from any incoming profile (supports edits).
  const deriveInitialStateFromProfile = useCallback((profile) => {
    const birthDateIso = profile?.birthDateIso ?? "";
    const hasAcknowledgedBirthDate =
      Boolean(profile?.hasAcknowledgedBirthDate) ?? false;

    const heightCentimeters =
      profile?.heightCentimeters != null &&
      Number.isFinite(Number(profile.heightCentimeters))
        ? String(profile.heightCentimeters)
        : "";

    const { feet: heightFeet, inches: heightInches } =
      centimetersToFeetInches(Number(profile?.heightCentimeters || 0));

    const weightKilograms =
      profile?.weightKilograms != null &&
      Number.isFinite(Number(profile.weightKilograms))
        ? String(profile.weightKilograms)
        : "";

    const weightPounds = kilogramsToPounds(
      Number(profile?.weightKilograms || 0),
    );

    // Weight-loss goal: Firestore keys weightLoss (boolean) + looseWeight (kg)
    const weightLoss =
      typeof profile?.weightLoss === "boolean"
        ? profile.weightLoss
        : false;

    const looseWeight =
      profile?.looseWeight != null &&
      Number.isFinite(Number(profile.looseWeight))
        ? String(profile.looseWeight)
        : "";

    return {
      birthDateIso,
      hasAcknowledgedBirthDate,
      sex: profile?.sex ?? "",
      heightCentimeters,
      heightFeet: heightFeet || "",
      heightInches: heightInches || "",
      weightKilograms,
      weightPounds: weightPounds || "",
      physicalActivityLevelId: profile?.physicalActivityLevelId ?? "",
      // Advanced activity (optional)
      advancedActivityTypeId: profile?.advancedActivityTypeId ?? "",
      advancedActivityMinutesPerDay:
        profile?.advancedActivityMinutesPerDay != null &&
        Number.isFinite(Number(profile.advancedActivityMinutesPerDay))
          ? String(profile.advancedActivityMinutesPerDay)
          : "",
      // Weight-loss focus
      weightLoss,
      looseWeight,
      // Meal preferences
      mealPref_dietStyle: profile?.mealPref_dietStyle ?? "none",
      mealPref_allergies: profile?.mealPref_allergies ?? [],
      mealPref_otherAllergy: profile?.mealPref_otherAllergy ?? "",
      mealPref_religious: profile?.mealPref_religious ?? [],
      mealPref_dislikes: profile?.mealPref_dislikes ?? [],
      mealPref_otherDislikes: profile?.mealPref_otherDislikes ?? "",
      mealPref_convenience: profile?.mealPref_convenience ?? [],
    };
  }, []);

  // First render state
  const initialState = useMemo(
    () => deriveInitialStateFromProfile(initialProfile || {}),
    [deriveInitialStateFromProfile, initialProfile],
  );

  const [profileState, setProfileState] = useState(initialState);

  // Keep the form in sync whenever the sheet opens or profile prop changes.
  useEffect(() => {
    if (!open) return;
    setProfileState(deriveInitialStateFromProfile(initialProfile || {}));
  }, [open, initialProfile, deriveInitialStateFromProfile]);

  // Unit toggles
  const [heightUnit, setHeightUnit] = useState(HEIGHT_UNITS.METRIC);
  const [weightUnit, setWeightUnit] = useState(WEIGHT_UNITS.METRIC);

  // Advanced mode mirrors incoming profile when opened
  const [isAdvancedActivityMode, setIsAdvancedActivityMode] = useState(
    Boolean(
      initialProfile?.advancedActivityTypeId ||
        initialProfile?.advancedActivityMinutesPerDay,
    ),
  );

  useEffect(() => {
    if (!open) return;
    setIsAdvancedActivityMode(
      Boolean(
        initialProfile?.advancedActivityTypeId ||
          initialProfile?.advancedActivityMinutesPerDay,
      ),
    );
  }, [open, initialProfile]);

  const [validationErrors, setValidationErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isMealPrefsDropdownOpen, setIsMealPrefsDropdownOpen] =
    useState(false);

  // Gate: profile must be completed if NOT explicitly true (catches false, undefined, missing)
  const mustCompleteProfile = initialProfile?.profileSetUpComplete !== true;
  const effectiveOpen = mustCompleteProfile ? true : open;
  const isProfileRequired = mustCompleteProfile;

  const selectedPhysicalActivityOption = useMemo(
    () =>
      physicalActivityOptions.find(
        (option) => option.id === profileState.physicalActivityLevelId,
      ),
    [profileState.physicalActivityLevelId],
  );

  const selectedAdvancedActivityType = useMemo(
    () =>
      advancedActivityTypeOptions.find(
        (option) => option.id === profileState.advancedActivityTypeId,
      ),
    [profileState.advancedActivityTypeId],
  );

  // Did the user have any advanced activity when the sheet opened?
  const userHadInitialAdvancedTraining = useMemo(
    () =>
      Boolean(
        initialProfile?.advancedActivityTypeId ||
          initialProfile?.advancedActivityMinutesPerDay ||
          initialProfile?.AdvancedActivityLevelType ||
          initialProfile?.AdvancedActivityLevel,
      ),
    [initialProfile],
  );

  // Does the user currently have advanced activity filled in?
  const userCurrentlyHasAdvancedTraining = useMemo(() => {
    const minutesValue = parseFloat(
      profileState.advancedActivityMinutesPerDay || "0",
    );
    const hasMinutes =
      !Number.isNaN(minutesValue) && minutesValue > 0;

    const hasType =
      profileState.advancedActivityTypeId &&
      profileState.advancedActivityTypeId !== ADVANCED_ACTIVITY_NONE_ID;

    return Boolean(hasMinutes || hasType);
  }, [
    profileState.advancedActivityMinutesPerDay,
    profileState.advancedActivityTypeId,
  ]);

  // Generic change handler
  const handleFieldChange = (fieldName) => (event) => {
    const nextValue =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;

    setProfileState((previousProfileState) => ({
      ...previousProfileState,
      [fieldName]: nextValue,
    }));
  };

  const handleSexChange = (sexId) => {
    setProfileState((previousProfileState) => ({
      ...previousProfileState,
      sex: sexId,
    }));
  };

  const handleActivityLevelChange = (activityLevelId) => {
    setProfileState((previousProfileState) => ({
      ...previousProfileState,
      physicalActivityLevelId: activityLevelId,
    }));
  };

  const handleAdvancedActivityTypeChange = (activityTypeId) => {
    setProfileState((previousProfileState) => ({
      ...previousProfileState,
      advancedActivityTypeId: activityTypeId,
    }));
  };

  const handleAdvancedActivityModeToggle = () => {
    setIsAdvancedActivityMode(
      (previousIsAdvancedActivityMode) =>
        !previousIsAdvancedActivityMode,
    );
  };

  const handleClearAdvancedTraining = () => {
    // Keep the section expanded, but clear current values and related errors.
    setIsAdvancedActivityMode(true);
    setProfileState((previousProfileState) => ({
      ...previousProfileState,
      advancedActivityTypeId: "",
      advancedActivityMinutesPerDay: "",
    }));
    setValidationErrors((previousValidationErrors) => {
      const {
        advancedActivityTypeId,
        advancedActivityMinutesPerDay,
        ...remainingValidationErrors
      } = previousValidationErrors || {};
      return remainingValidationErrors;
    });
  };

  // Meal preferences handlers
  const handleMealPrefsDropdownToggle = () => {
    setIsMealPrefsDropdownOpen((previousIsOpen) => !previousIsOpen);
  };

  const handleMealPrefDietStyleChange = (dietStyle) => {
    setProfileState((previousState) => ({
      ...previousState,
      mealPref_dietStyle: dietStyle,
    }));
  };

  const handleMealPrefToggle = (category, value) => {
    setProfileState((previousState) => {
      const currentArray = previousState[category] || [];
      const nextArray = currentArray.includes(value)
        ? currentArray.filter((currentValue) => currentValue !== value)
        : [...currentArray, value];

      return {
        ...previousState,
        [category]: nextArray,
      };
    });
  };

  const buildMealPrefsSummary = () => {
    const selections = [];
    if (
      profileState.mealPref_dietStyle &&
      profileState.mealPref_dietStyle !== "none"
    ) {
      const option = mealPrefDietStyleOptions.find(
        (dietOption) => dietOption.id === profileState.mealPref_dietStyle,
      );
      if (option) selections.push(option.label);
    }
    profileState.mealPref_allergies?.forEach((allergyId) => {
      const option = mealPrefAllergyOptions.find(
        (allergyOption) => allergyOption.id === allergyId,
      );
      if (option) selections.push(option.label);
    });
    profileState.mealPref_religious?.forEach((religiousId) => {
      const option = mealPrefReligiousOptions.find(
        (religiousOption) => religiousOption.id === religiousId,
      );
      if (option) selections.push(option.label);
    });
    profileState.mealPref_dislikes?.forEach((dislikeId) => {
      const option = mealPrefDislikeOptions.find(
        (dislikeOption) => dislikeOption.id === dislikeId,
      );
      if (option) selections.push(option.label);
    });
    profileState.mealPref_convenience?.forEach((convenienceId) => {
      const option = mealPrefConvenienceOptions.find(
        (convenienceOption) => convenienceOption.id === convenienceId,
      );
      if (option) selections.push(option.label);
    });
    if (selections.length === 0) {
      return UI_COPY.mealPreferences.triggerPlaceholder;
    }
    if (selections.length <= 3) {
      return selections.join(" · ");
    }
    return `${selections
      .slice(0, 3)
      .join(" · ")} · +${selections.length - 3} more`;
  };

  // When toggling height unit, convert values for UX (but save logic uses active unit anyway)
  const handleHeightUnitToggle = () => {
    setHeightUnit((previousHeightUnit) => {
      const nextHeightUnit =
        previousHeightUnit === HEIGHT_UNITS.METRIC
          ? HEIGHT_UNITS.IMPERIAL
          : HEIGHT_UNITS.METRIC;
      setProfileState((previousState) => {
        if (nextHeightUnit === HEIGHT_UNITS.IMPERIAL) {
          const centimetersValue = parseFloat(
            previousState.heightCentimeters || "0",
          );
          const { feet, inches } =
            centimetersToFeetInches(centimetersValue);
          return {
            ...previousState,
            heightFeet: feet || "",
            heightInches: inches || "",
          };
        }

        const centimetersValue = convertFeetAndInchesToCentimeters(
          previousState.heightFeet,
          previousState.heightInches,
        );
        return {
          ...previousState,
          heightCentimeters: centimetersValue
            ? String(Math.round(centimetersValue))
            : "",
        };
      });
      return nextHeightUnit;
    });
  };

  // When toggling weight unit, convert values for UX (save logic uses active unit)
  const handleWeightUnitToggle = () => {
    setWeightUnit((previousWeightUnit) => {
      const nextWeightUnit =
        previousWeightUnit === WEIGHT_UNITS.METRIC
          ? WEIGHT_UNITS.IMPERIAL
          : WEIGHT_UNITS.METRIC;
      setProfileState((previousState) => {
        if (nextWeightUnit === WEIGHT_UNITS.IMPERIAL) {
          const poundsValue = kilogramsToPounds(
            parseFloat(previousState.weightKilograms || "0"),
          );
          return { ...previousState, weightPounds: poundsValue || "" };
        }

        const kilogramsValue = convertPoundsToKilograms(
          previousState.weightPounds,
        );
        return {
          ...previousState,
          weightKilograms: kilogramsValue
            ? String(Math.round(kilogramsValue))
            : "",
        };
      });
      return nextWeightUnit;
    });
  };

  // Validation
  const validateProfile = () => {
    const errors = {};

    if (!profileState.birthDateIso) {
      errors.birthDateIso = VALIDATION_MESSAGES.birthDateIso;
    }
    if (!profileState.hasAcknowledgedBirthDate) {
      errors.hasAcknowledgedBirthDate =
        VALIDATION_MESSAGES.hasAcknowledgedBirthDate;
    }
    if (!profileState.sex) {
      errors.sex = VALIDATION_MESSAGES.sex;
    }

    // Height valid if current unit has a valid entry
    let hasHeight = false;
    if (heightUnit === HEIGHT_UNITS.METRIC) {
      const centimetersValue = parseFloat(
        profileState.heightCentimeters || "0",
      );
      hasHeight = !Number.isNaN(centimetersValue) && centimetersValue > 0;
    } else {
      const feetValue = parseFloat(profileState.heightFeet || "0");
      const inchesValue = parseFloat(profileState.heightInches || "0");
      hasHeight = feetValue > 0 || inchesValue > 0;
    }
    if (!hasHeight) {
      errors.height = VALIDATION_MESSAGES.height;
    }

    // Weight valid if current unit has a valid entry
    let hasWeight = false;
    if (weightUnit === WEIGHT_UNITS.METRIC) {
      const kilogramsValue = parseFloat(
        profileState.weightKilograms || "0",
      );
      hasWeight =
        !Number.isNaN(kilogramsValue) && kilogramsValue > 0;
    } else {
      const poundsValue = parseFloat(profileState.weightPounds || "0");
      hasWeight =
        !Number.isNaN(poundsValue) && poundsValue > 0;
    }
    if (!hasWeight) {
      errors.weight = VALIDATION_MESSAGES.weight;
    }

    if (!profileState.physicalActivityLevelId) {
      errors.physicalActivityLevelId =
        VALIDATION_MESSAGES.physicalActivityLevelId;
    }

    // Weight-loss goal is optional overall; if enabled, require a valid number.
    if (profileState.weightLoss) {
      const looseWeightValue = parseFloat(
        profileState.looseWeight || "0",
      );
      if (
        Number.isNaN(looseWeightValue) ||
        looseWeightValue <= 0
      ) {
        errors.looseWeight = VALIDATION_MESSAGES.looseWeight;
      }
    }

    // Advanced section is optional even when expanded:
    // Only enforce fields if the user actually entered a training type (not "none") or minutes > 0.
    if (isAdvancedActivityMode) {
      const minutesValue = parseFloat(
        profileState.advancedActivityMinutesPerDay || "0",
      );
      const wantsTraining =
        (profileState.advancedActivityTypeId &&
          profileState.advancedActivityTypeId !==
            ADVANCED_ACTIVITY_NONE_ID) ||
        minutesValue > 0;

      if (wantsTraining) {
        if (
          !profileState.advancedActivityTypeId ||
          profileState.advancedActivityTypeId ===
            ADVANCED_ACTIVITY_NONE_ID
        ) {
          errors.advancedActivityTypeId =
            VALIDATION_MESSAGES.advancedActivityTypeId;
        }
        if (Number.isNaN(minutesValue) || minutesValue <= 0) {
          errors.advancedActivityMinutesPerDay =
            VALIDATION_MESSAGES.advancedActivityMinutesPerDay;
        }
      }
    }

    return errors;
  };

  // Normalize profile to canonical units, prioritizing the VISIBLE/ACTIVE unit.
  const buildCanonicalProfile = () => {
    const birthDateIso = profileState.birthDateIso || undefined;
    const hasAcknowledgedBirthDate = Boolean(
      profileState.hasAcknowledgedBirthDate,
    );

    // Height → cm: use the active unit explicitly
    let heightCentimeters;
    if (heightUnit === HEIGHT_UNITS.METRIC) {
      const centimetersValue = parseFloat(
        profileState.heightCentimeters || "0",
      );
      heightCentimeters =
        !Number.isNaN(centimetersValue) && centimetersValue > 0
          ? centimetersValue
          : undefined;
    } else {
      heightCentimeters = convertFeetAndInchesToCentimeters(
        profileState.heightFeet,
        profileState.heightInches,
      );
    }

    // Weight → kg: use the active unit explicitly
    let weightKilograms;
    if (weightUnit === WEIGHT_UNITS.METRIC) {
      const kilogramsValue = parseFloat(
        profileState.weightKilograms || "0",
      );
      weightKilograms =
        !Number.isNaN(kilogramsValue) && kilogramsValue > 0
          ? kilogramsValue
          : undefined;
    } else {
      weightKilograms = convertPoundsToKilograms(
        profileState.weightPounds,
      );
    }

    // Advanced training: treat "none" or 0/blank as undefined (i.e., clear)
    const typeRaw = profileState.advancedActivityTypeId || "";
    const minutesRaw = parseFloat(
      profileState.advancedActivityMinutesPerDay || "0",
    );

    const advancedActivityTypeId =
      typeRaw && typeRaw !== ADVANCED_ACTIVITY_NONE_ID
        ? typeRaw
        : undefined;

    let advancedActivityMinutesPerDay;
    if (!Number.isNaN(minutesRaw) && minutesRaw > 0) {
      advancedActivityMinutesPerDay = minutesRaw;
    }

    const physicalActivityLevelId =
      profileState.physicalActivityLevelId || undefined;

    // Weight-loss goal (boolean + kg, kg optional)
    const weightLoss = Boolean(profileState.weightLoss);
    let looseWeight;
    const looseWeightValue = parseFloat(
      profileState.looseWeight || "0",
    );
    if (!Number.isNaN(looseWeightValue) && looseWeightValue > 0) {
      looseWeight = looseWeightValue;
    }

    return {
      birthDateIso,
      hasAcknowledgedBirthDate,
      sex: profileState.sex || undefined,
      heightCentimeters,
      weightKilograms,
      physicalActivityLevelId,
      advancedActivityTypeId,
      advancedActivityMinutesPerDay,
      weightLoss,
      looseWeight,
      // Meal preferences
      mealPref_dietStyle: profileState.mealPref_dietStyle || "none",
      mealPref_allergies: profileState.mealPref_allergies || [],
      mealPref_otherAllergy:
        profileState.mealPref_otherAllergy || "",
      mealPref_religious: profileState.mealPref_religious || [],
      mealPref_dislikes: profileState.mealPref_dislikes || [],
      mealPref_otherDislikes:
        profileState.mealPref_otherDislikes || "",
      mealPref_convenience:
        profileState.mealPref_convenience || [],
    };
  };

  // Save (supports first-time + edits). Writes canonical + mirrored fields.
  const handleSaveClick = async () => {
    const errors = validateProfile();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    setIsSaving(true);

    const canonicalProfile = buildCanonicalProfile();

    // Remove undefined (Firestore rejects them)
    const canonicalForFirestore = Object.fromEntries(
      Object.entries(canonicalProfile).filter(
        ([, value]) => value !== undefined,
      ),
    );

    // Mirror/derived values (based on canonical values which now honor active unit)
    const heightCmRaw =
      typeof canonicalProfile.heightCentimeters === "number" &&
      Number.isFinite(canonicalProfile.heightCentimeters)
        ? canonicalProfile.heightCentimeters
        : 0;

    const heightCmInt = heightCmRaw > 0 ? Math.round(heightCmRaw) : 0;

    let heightFtInt = 0;
    let heightInchInt = 0;
    if (heightCmInt > 0) {
      const totalInches = heightCmInt / 2.54;
      heightFtInt = Math.floor(totalInches / 12);
      heightInchInt = Math.round(
        totalInches - heightFtInt * 12,
      );
    } else {
      // Fallback to raw ft/in inputs (should be rare because of validation)
      const feetValue = parseInt(
        profileState.heightFeet || "0",
        10,
      );
      const inchesValue = parseInt(
        profileState.heightInches || "0",
        10,
      );
      if (!Number.isNaN(feetValue) && feetValue > 0) {
        heightFtInt = feetValue;
      }
      if (!Number.isNaN(inchesValue) && inchesValue > 0) {
        heightInchInt = inchesValue;
      }
    }

    const weightKgRaw =
      typeof canonicalProfile.weightKilograms === "number" &&
      Number.isFinite(canonicalProfile.weightKilograms)
        ? canonicalProfile.weightKilograms
        : 0;

    let weightKgInt = weightKgRaw > 0 ? Math.round(weightKgRaw) : 0;
    let weightLbInt = 0;
    if (weightKgInt > 0) {
      weightLbInt = Math.round(weightKgInt / 0.45359237);
    } else {
      const rawPounds = parseFloat(
        profileState.weightPounds || "0",
      );
      if (!Number.isNaN(rawPounds) && rawPounds > 0) {
        weightLbInt = Math.round(rawPounds);
        weightKgInt = Math.round(rawPounds * 0.45359237);
      }
    }

    const activityLevel =
      canonicalProfile.physicalActivityLevelId &&
      ACTIVITY_LEVEL_MAP[canonicalProfile.physicalActivityLevelId]
        ? ACTIVITY_LEVEL_MAP[
            canonicalProfile.physicalActivityLevelId
          ]
        : 0;

    const advancedMinutes =
      typeof canonicalProfile.advancedActivityMinutesPerDay ===
        "number" &&
      Number.isFinite(
        canonicalProfile.advancedActivityMinutesPerDay,
      )
        ? Math.round(
            canonicalProfile.advancedActivityMinutesPerDay,
          )
        : 0;

    const shouldClearAdvanced =
      canonicalProfile.advancedActivityTypeId === undefined &&
      canonicalProfile.advancedActivityMinutesPerDay ===
        undefined;

    const finalProfile = {
      ...canonicalProfile,
      profileSetUpComplete: true,
    };

    if (currentUser?.uid) {
      const documentReference = doc(
        db,
        FIRESTORE_COLLECTIONS.users,
        currentUser.uid,
      );

      // Minimal write first to keep the entitlement/profile gate open
      try {
        await setDoc(
          documentReference,
          { profileSetUpComplete: true },
          { merge: true },
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(ERROR_MESSAGES.setProfileComplete, error);
      }

      // Full merge write with canonical and mirrored fields
      try {
        // Base payload
        let updatePayload = {
          ...canonicalForFirestore,
          profileSetUpComplete: true,

          // Explicit mirrors (requested names)
          dateOfBirth: canonicalProfile.birthDateIso || "",
          confirmDob: !!canonicalProfile.hasAcknowledgedBirthDate,
          Sex: canonicalProfile.sex || "",

          heightCm: heightCmInt,
          heightFt: heightFtInt,
          heightInch: heightInchInt,

          weightKg: weightKgInt,
          weightLb: weightLbInt,

          activityLevel, // 1/2/3

          // Mirrors for advanced training (will be overridden to delete if clearing)
          AdvancedActivityLevelType:
            canonicalProfile.advancedActivityTypeId || "",
          AdvancedActivityLevel: advancedMinutes, // minutes/day
        };

        // If clearing training, actively delete both canonical and mirror fields
        if (shouldClearAdvanced) {
          updatePayload = {
            ...updatePayload,
            advancedActivityTypeId: deleteField(),
            advancedActivityMinutesPerDay: deleteField(),
            AdvancedActivityLevelType: deleteField(),
            AdvancedActivityLevel: deleteField(),
          };
        }

        await setDoc(documentReference, updatePayload, {
          merge: true,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(ERROR_MESSAGES.saveProfile, error);
      }
    } else {
      // eslint-disable-next-line no-console
      console.error(ERROR_MESSAGES.missingUser);
    }

    // Keep local + parent in sync so subsequent opens show fresh values
    setProfileState(deriveInitialStateFromProfile(finalProfile));
    if (onSaveProfile) {
      onSaveProfile(finalProfile);
    }

    setIsSaving(false);
    if (onClose && !isProfileRequired) {
      onClose();
    }
  };

  const handleCancelClick = async () => {
    if (isProfileRequired) {
      // User has incomplete profile (profileSetUpComplete === false or missing).
      // Sign them out automatically and redirect to home.
      try {
        await logout();
        navigate("/");
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to sign out on cancel:", error);
      }
      return;
    }
    if (onClose) onClose();
  };

  const hasAnyErrors = Object.keys(validationErrors).length > 0;

  // When a user originally had advanced training and it is now cleared,
  // we show "Save changes" instead of "Save profile" and style it as a green button.
  const hasClearedExistingAdvancedTraining =
    userHadInitialAdvancedTraining &&
    !userCurrentlyHasAdvancedTraining;

  // Clear training is only clickable when there is something to clear and not saving.
  const shouldDisableClearTrainingButton =
    !userCurrentlyHasAdvancedTraining || isSaving;

  // Build the save button classes so we can apply a green variant when needed.
  const saveButtonClassName = [
    "sheet-action-button",
    "sheet-action-save",
    hasClearedExistingAdvancedTraining
      ? "sheet-action-save-cleared"
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  const primaryActionButtonLabel = isSaving
    ? BUTTON_LABELS.saving
    : hasClearedExistingAdvancedTraining
    ? BUTTON_LABELS.saveChanges
    : BUTTON_LABELS.saveProfile;

  // Dynamic helper text for weight based on the active unit
  const savingWeightCopy =
    UI_COPY.weight.savingCopy[weightUnit] || "";

  // Build account link items dynamically so we do not hard-code them in JSX
  const accountLinkItems = [
    UI_COPY.accountLinks.changePlan,
    UI_COPY.accountLinks.billing,
    UI_COPY.accountLinks.deleteAccount,
  ].filter(Boolean);

  return (
    <Sheet open={effectiveOpen}>
      {/* Backdrop without click handler, so user cannot close by clicking outside */}
      {effectiveOpen && <div className="sheet-backdrop" />}

      {effectiveOpen ? (
        <SheetContent
          role="dialog"
          aria-modal="true"
          aria-labelledby="user-settings-title"
        >
          <SheetHeader>
            <SheetTitle id="user-settings-title">
              {UI_COPY.sheetTitle}
            </SheetTitle>
          </SheetHeader>

          <div className="sheet-body">
            {/* Date of Birth */}
            <div
              className={`field-group field-group-birthdate ${
                validationErrors.birthDateIso ||
                validationErrors.hasAcknowledgedBirthDate
                  ? "field-group-error"
                  : ""
              }`}
            >
              <label
                className="field-label"
                htmlFor="birth-date"
              >
                {UI_COPY.birthDate.label}
              </label>
              <Input
                id="birth-date"
                type="date"
                value={profileState.birthDateIso}
                onChange={handleFieldChange("birthDateIso")}
                className={`field-input field-input-birthdate ${
                  validationErrors.birthDateIso
                    ? "field-input-error"
                    : ""
                }`}
              />
              <p className="text-xs text-muted field-helper-text">
                {UI_COPY.birthDate.helper}
              </p>
              <label className="acknowledge-row">
                <input
                  type="checkbox"
                  checked={
                    profileState.hasAcknowledgedBirthDate
                  }
                  onChange={handleFieldChange(
                    "hasAcknowledgedBirthDate",
                  )}
                />
                <span className="text-xs">
                  {UI_COPY.birthDate.acknowledgement}
                </span>
              </label>
            </div>

            {/* Sex */}
            <div
              className={`field-group field-group-sex ${
                validationErrors.sex ? "field-group-error" : ""
              }`}
            >
              <div className="sex-row">
                <div className="field-label field-label-sex">
                  {UI_COPY.sex.label}
                </div>

                <div className="radio-row">
                  {sexOptions.map((sexOption) => (
                    <label
                      key={sexOption.id}
                      className="radio-item sex-radio-item"
                    >
                      <input
                        type="radio"
                        name="profile-sex"
                        aria-label={sexOption.label}
                        checked={
                          profileState.sex === sexOption.id
                        }
                        onChange={() =>
                          handleSexChange(sexOption.id)
                        }
                      />
                      <span className="text-sm">
                        {sexOption.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <p className="text-xs text-muted sex-helper-text">
                {UI_COPY.sex.helper}
              </p>
            </div>

            {/* Height */}
            <div
              className={`field-group field-group-height ${
                validationErrors.height
                  ? "field-group-error"
                  : ""
              }`}
            >
              <div className="height-row">
                <span className="field-label">
                  {UI_COPY.height.label}
                </span>
                <button
                  type="button"
                  onClick={handleHeightUnitToggle}
                  className="height-toggle-button"
                  aria-label={
                    UI_COPY.height.toggleAriaLabel
                  }
                >
                  {UI_COPY.height.toggleLabels[heightUnit]}
                </button>
                <span className="height-colon">:</span>

                {heightUnit === HEIGHT_UNITS.METRIC ? (
                  <div className="height-input-row height-input-row-metric">
                    <Input
                      id="height-cm"
                      type="number"
                      value={profileState.heightCentimeters}
                      onChange={handleFieldChange(
                        "heightCentimeters",
                      )}
                      className={`field-input field-input-height-cm ${
                        validationErrors.height
                          ? "field-input-error"
                          : ""
                      }`}
                    />
                    <span className="text-xs height-unit-label">
                      {
                        UI_COPY.height.unitLabels[
                          HEIGHT_UNITS.METRIC
                        ]
                      }
                    </span>
                  </div>
                ) : (
                  <div className="height-input-row height-input-row-imperial">
                    <div className="height-input-group height-input-group-ft">
                      <span className="text-xs height-unit-label">
                        {
                          UI_COPY.height.unitLabels
                            .imperialFeet
                        }
                      </span>
                      <Input
                        id="height-ft"
                        type="number"
                        value={profileState.heightFeet}
                        onChange={handleFieldChange(
                          "heightFeet",
                        )}
                        className={`field-input field-input-height-ft ${
                          validationErrors.height
                            ? "field-input-error"
                            : ""
                        }`}
                      />
                    </div>
                    <div className="height-input-group height-input-group-in">
                      <span className="text-xs height-unit-label">
                        {
                          UI_COPY.height.unitLabels
                            .imperialInches
                        }
                      </span>
                      <Input
                        id="height-in"
                        type="number"
                        value={profileState.heightInches}
                        onChange={handleFieldChange(
                          "heightInches",
                        )}
                        className={`field-input field-input-height-in ${
                          validationErrors.height
                            ? "field-input-error"
                            : ""
                        }`}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Current weight */}
            <div
              className={`field-group field-group-weight ${
                validationErrors.weight
                  ? "field-group-error"
                  : ""
              }`}
            >
              <div className="weight-row">
                <span className="field-label">
                  {UI_COPY.weight.label}
                </span>
                <button
                  type="button"
                  onClick={handleWeightUnitToggle}
                  className="weight-toggle-button"
                  aria-label={
                    UI_COPY.weight.toggleAriaLabel
                  }
                >
                  {UI_COPY.weight.toggleLabels[weightUnit]}
                </button>
                <span className="weight-colon">:</span>

                {weightUnit === WEIGHT_UNITS.METRIC ? (
                  <div className="weight-input-row weight-input-row-kg">
                    <Input
                      id="weight-kg"
                      type="number"
                      value={profileState.weightKilograms}
                      onChange={handleFieldChange(
                        "weightKilograms",
                      )}
                      className={`field-input field-input-weight-kg ${
                        validationErrors.weight
                          ? "field-input-error"
                          : ""
                      }`}
                    />
                    <span className="text-xs weight-unit-label">
                      {
                        UI_COPY.weight.unitLabels[
                          WEIGHT_UNITS.METRIC
                        ]
                      }
                    </span>
                  </div>
                ) : (
                  <div className="weight-input-row weight-input-row-lb">
                    <Input
                      id="weight-lb"
                      type="number"
                      value={profileState.weightPounds}
                      onChange={handleFieldChange(
                        "weightPounds",
                      )}
                      className={`field-input field-input-weight-lb ${
                        validationErrors.weight
                          ? "field-input-error"
                          : ""
                      }`}
                    />
                    <span className="text-xs weight-unit-label">
                      {
                        UI_COPY.weight.unitLabels[
                          WEIGHT_UNITS.IMPERIAL
                        ]
                      }
                    </span>
                  </div>
                )}
              </div>

              <p
                className="text-xs text-muted weight-helper-text"
                aria-live="polite"
              >
                {savingWeightCopy}
              </p>
            </div>

            {/* Weight loss focus (optional) */}
            <div
              className={`field-group field-group-weight-loss ${
                validationErrors.looseWeight
                  ? "field-group-error"
                  : ""
              }`}
            >
              <div className="weight-loss-header">
                <span className="field-label">
                  {UI_COPY.weightLoss.sectionLabel}
                </span>
              </div>
              <p className="text-xs text-muted field-helper-text">
                {UI_COPY.weightLoss.helper}
              </p>

              <label className="weight-loss-toggle-row">
                <input
                  type="checkbox"
                  checked={Boolean(profileState.weightLoss)}
                  onChange={handleFieldChange("weightLoss")}
                  aria-label={
                    UI_COPY.weightLoss.toggle.ariaLabel
                  }
                />
                <span className="text-sm">
                  {UI_COPY.weightLoss.toggle.label}
                </span>
              </label>
              <p className="text-xs text-muted">
                {profileState.weightLoss
                  ? UI_COPY.weightLoss.toggle.onCopy
                  : UI_COPY.weightLoss.toggle.offCopy}
              </p>

              {profileState.weightLoss && (
                <div className="weight-loss-goal">
                  <label
                    htmlFor="weight-loss-goal"
                    className="text-xs text-muted weight-loss-goal-label"
                  >
                    {UI_COPY.weightLoss.goal.label}
                  </label>
                  <div className="weight-loss-goal-row">
                    <Input
                      id="weight-loss-goal"
                      type="number"
                      min="0"
                      value={profileState.looseWeight}
                      onChange={handleFieldChange(
                        "looseWeight",
                      )}
                      className={`field-input field-input-weight-loss-goal ${
                        validationErrors.looseWeight
                          ? "field-input-error"
                          : ""
                      }`}
                    />
                    <span className="text-xs weight-loss-unit-label">
                      {UI_COPY.weightLoss.goal.unitLabel}
                    </span>
                  </div>
                  <p className="text-xs text-muted">
                    {UI_COPY.weightLoss.goal.helper}
                  </p>
                  <p className="text-xs text-muted">
                    {UI_COPY.weightLoss.info.bmiTileHint}
                  </p>
                  <p className="text-xs text-muted">
                    {UI_COPY.weightLoss.info.safetyHint}
                  </p>
                </div>
              )}
            </div>

            {/* Physical Activity */}
            <div
              className={`field-group field-group-activity ${
                validationErrors.physicalActivityLevelId ||
                validationErrors.advancedActivityTypeId ||
                validationErrors.advancedActivityMinutesPerDay
                  ? "field-group-error"
                  : ""
              }`}
            >
              {/* Label + Advanced button inline */}
              <div className="field-label activity-label-row">
                {UI_COPY.activity.label}{" "}
                <button
                  type="button"
                  onClick={handleAdvancedActivityModeToggle}
                  className="activity-advanced-toggle-button"
                  aria-pressed={isAdvancedActivityMode}
                  aria-label={
                    UI_COPY.activity.advancedToggle.ariaLabel
                  }
                >
                  {UI_COPY.activity.advancedToggle.prefix}{" "}
                  {isAdvancedActivityMode
                    ? UI_COPY.activity.advancedToggle.on
                    : UI_COPY.activity.advancedToggle.off}
                </button>
              </div>

              <p className="text-xs text-muted activity-helper-text">
                {UI_COPY.activity.helper}
              </p>

              {/* Baseline level dropdown */}
              <div className="activity-baseline">
                <label
                  htmlFor="activity-level-select"
                  className="text-xs text-muted activity-baseline-label"
                >
                  {UI_COPY.activity.baselineLabel}
                </label>
                <select
                  id="activity-level-select"
                  name="activity-level"
                  className={`activity-select ${
                    validationErrors.physicalActivityLevelId
                      ? "field-input-error"
                      : ""
                  }`}
                  value={
                    profileState.physicalActivityLevelId
                  }
                  onChange={(event) =>
                    handleActivityLevelChange(
                      event.target.value,
                    )
                  }
                >
                  <option value="">
                    {UI_COPY.activity.baselinePlaceholder}
                  </option>
                  {physicalActivityOptions.map((option) => (
                    <option
                      key={option.id}
                      value={option.id}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>

                {selectedPhysicalActivityOption && (
                  <p className="text-xs text-muted activity-baseline-helper-text">
                    {
                      selectedPhysicalActivityOption.helperText
                    }
                  </p>
                )}
              </div>

              {/* Advanced structured training inputs */}
              {isAdvancedActivityMode && (
                <div className="activity-advanced">
                  <button
                    type="button"
                    onClick={handleClearAdvancedTraining}
                    className="activity-clear-button"
                    aria-label={
                      UI_COPY.activity.clearButton.ariaLabel
                    }
                    title={
                      UI_COPY.activity.clearButton.title
                    }
                    disabled={
                      shouldDisableClearTrainingButton
                    }
                  >
                    {UI_COPY.activity.clearButton.label}
                  </button>

                  <div className="text-xs text-muted activity-advanced-intro">
                    {UI_COPY.activity.advancedIntro}
                  </div>

                  <label
                    htmlFor="advanced-activity-type"
                    className="text-xs text-muted activity-advanced-label"
                  >
                    {UI_COPY.activity.advancedLabel}
                  </label>
                  <select
                    id="advanced-activity-type"
                    className={`activity-advanced-select ${
                      validationErrors.advancedActivityTypeId
                        ? "field-input-error"
                        : ""
                    }`}
                    value={
                      profileState.advancedActivityTypeId
                    }
                    onChange={(event) =>
                      handleAdvancedActivityTypeChange(
                        event.target.value,
                      )
                    }
                  >
                    <option value="">
                      {UI_COPY.activity.advancedPlaceholder}
                    </option>
                    {advancedActivityTypeOptions.map(
                      (option) => (
                        <option
                          key={option.id}
                          value={option.id}
                        >
                          {option.label}
                        </option>
                      ),
                    )}
                  </select>

                  {selectedAdvancedActivityType && (
                    <p className="text-xs text-muted activity-advanced-example">
                      {selectedAdvancedActivityType.example}
                    </p>
                  )}

                  <label
                    htmlFor="advanced-activity-minutes"
                    className="text-xs text-muted activity-advanced-minutes-label"
                  >
                    {UI_COPY.activity.minutesLabel}
                  </label>
                  <div className="activity-advanced-minutes-row">
                    <Input
                      id="advanced-activity-minutes"
                      type="number"
                      min="0"
                      className={`field-input field-input-advanced-activity-minutes ${
                        validationErrors.advancedActivityMinutesPerDay
                          ? "field-input-error"
                          : ""
                      }`}
                      value={
                        profileState.advancedActivityMinutesPerDay
                      }
                      onChange={handleFieldChange(
                        "advancedActivityMinutesPerDay",
                      )}
                    />
                    <span className="text-xs text-muted activity-advanced-minutes-unit">
                      {UI_COPY.activity.minutesUnitLabel}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Meal & ingredient preferences */}
            <div className="field-group field-group-meal-prefs">
              <label
                className="field-label"
                htmlFor="meal-prefs-trigger"
              >
                {UI_COPY.mealPreferences.label}
              </label>
              <p className="text-xs text-muted field-helper-text">
                {UI_COPY.mealPreferences.helper}
              </p>
              <button
                type="button"
                id="meal-prefs-trigger"
                className={`meal-prefs-dropdown-trigger ${
                  isMealPrefsDropdownOpen ? "open" : ""
                }`}
                onClick={handleMealPrefsDropdownToggle}
                aria-expanded={isMealPrefsDropdownOpen}
              >
                <span className="meal-prefs-summary">
                  {buildMealPrefsSummary()}
                </span>
                <span className="meal-prefs-arrow">
                  {isMealPrefsDropdownOpen ? "▲" : "▼"}
                </span>
              </button>
              {isMealPrefsDropdownOpen && (
                <div className="meal-prefs-dropdown">
                  <div className="meal-prefs-category">
                    <h4 className="meal-prefs-category-header">
                      {
                        UI_COPY.mealPreferences
                          .dietStyleHeader
                      }
                    </h4>
                    <div className="meal-prefs-options">
                      {mealPrefDietStyleOptions.map(
                        (option) => (
                          <label
                            key={option.id}
                            className="meal-prefs-option"
                          >
                            <input
                              type="radio"
                              name="meal-pref-diet-style"
                              value={option.id}
                              checked={
                                profileState.mealPref_dietStyle ===
                                option.id
                              }
                              onChange={() =>
                                handleMealPrefDietStyleChange(
                                  option.id,
                                )
                              }
                            />
                            <span className="meal-prefs-option-label">
                              {option.label}
                              {option.description && (
                                <span className="text-xs meal-prefs-option-description">
                                  {option.description}
                                </span>
                              )}
                            </span>
                          </label>
                        ),
                      )}
                    </div>
                  </div>
                  <div className="meal-prefs-category">
                    <h4 className="meal-prefs-category-header">
                      {
                        UI_COPY.mealPreferences
                          .allergiesHeader
                      }
                    </h4>
                    <p className="text-xs meal-prefs-category-helper">
                      {
                        UI_COPY.mealPreferences
                          .allergiesHelper
                      }
                    </p>
                    <div className="meal-prefs-options">
                      {mealPrefAllergyOptions.map(
                        (option) => (
                          <label
                            key={option.id}
                            className="meal-prefs-option"
                          >
                            <input
                              type="checkbox"
                              checked={
                                profileState.mealPref_allergies?.includes(
                                  option.id,
                                ) || false
                              }
                              onChange={() =>
                                handleMealPrefToggle(
                                  "mealPref_allergies",
                                  option.id,
                                )
                              }
                            />
                            <span className="meal-prefs-option-label">
                              {option.label}
                            </span>
                          </label>
                        ),
                      )}
                    </div>
                    <label className="meal-prefs-text-label">
                      {
                        UI_COPY.mealPreferences
                          .otherAllergyLabel
                      }
                      <Input
                        type="text"
                        className="meal-prefs-text-input"
                        placeholder={
                          UI_COPY.mealPreferences
                            .otherAllergyPlaceholder
                        }
                        value={
                          profileState.mealPref_otherAllergy
                        }
                        onChange={handleFieldChange(
                          "mealPref_otherAllergy",
                        )}
                      />
                    </label>
                  </div>
                  <div className="meal-prefs-category">
                    <h4 className="meal-prefs-category-header">
                      {
                        UI_COPY.mealPreferences
                          .religiousHeader
                      }
                    </h4>
                    <p className="text-xs meal-prefs-category-helper">
                      {
                        UI_COPY.mealPreferences
                          .religiousHelper
                      }
                    </p>
                    <div className="meal-prefs-options">
                      {mealPrefReligiousOptions.map(
                        (option) => (
                          <label
                            key={option.id}
                            className="meal-prefs-option"
                          >
                            <input
                              type="checkbox"
                              checked={
                                profileState.mealPref_religious?.includes(
                                  option.id,
                                ) || false
                              }
                              onChange={() =>
                                handleMealPrefToggle(
                                  "mealPref_religious",
                                  option.id,
                                )
                              }
                            />
                            <span className="meal-prefs-option-label">
                              {option.label}
                              {option.description && (
                                <span className="text-xs meal-prefs-option-description">
                                  {option.description}
                                </span>
                              )}
                            </span>
                          </label>
                        ),
                      )}
                    </div>
                  </div>
                  <div className="meal-prefs-category">
                    <h4 className="meal-prefs-category-header">
                      {
                        UI_COPY.mealPreferences
                          .dislikesHeader
                      }
                    </h4>
                    <p className="text-xs meal-prefs-category-helper">
                      {
                        UI_COPY.mealPreferences
                          .dislikesHelper
                      }
                    </p>
                    <div className="meal-prefs-options">
                      {mealPrefDislikeOptions.map(
                        (option) => (
                          <label
                            key={option.id}
                            className="meal-prefs-option"
                          >
                            <input
                              type="checkbox"
                              checked={
                                profileState.mealPref_dislikes?.includes(
                                  option.id,
                                ) || false
                              }
                              onChange={() =>
                                handleMealPrefToggle(
                                  "mealPref_dislikes",
                                  option.id,
                                )
                              }
                            />
                            <span className="meal-prefs-option-label">
                              {option.label}
                            </span>
                          </label>
                        ),
                      )}
                    </div>
                    <label className="meal-prefs-text-label">
                      {
                        UI_COPY.mealPreferences
                          .otherDislikesLabel
                      }
                      <Input
                        type="text"
                        className="meal-prefs-text-input"
                        placeholder={
                          UI_COPY.mealPreferences
                            .otherDislikesPlaceholder
                        }
                        value={
                          profileState.mealPref_otherDislikes
                        }
                        onChange={handleFieldChange(
                          "mealPref_otherDislikes",
                        )}
                      />
                    </label>
                  </div>
                  <div className="meal-prefs-category">
                    <h4 className="meal-prefs-category-header">
                      {
                        UI_COPY.mealPreferences
                          .convenienceHeader
                      }
                    </h4>
                    <p className="text-xs meal-prefs-category-helper">
                      {
                        UI_COPY.mealPreferences
                          .convenienceHelper
                      }
                    </p>
                    <div className="meal-prefs-options">
                      {mealPrefConvenienceOptions.map(
                        (option) => (
                          <label
                            key={option.id}
                            className="meal-prefs-option"
                          >
                            <input
                              type="checkbox"
                              checked={
                                profileState.mealPref_convenience?.includes(
                                  option.id,
                                ) || false
                              }
                              onChange={() =>
                                handleMealPrefToggle(
                                  "mealPref_convenience",
                                  option.id,
                                )
                              }
                            />
                            <span className="meal-prefs-option-label">
                              {option.label}
                            </span>
                          </label>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Validation summary */}
            {hasAnyErrors && (
              <div className="validation-error-summary">
                <div className="validation-error-summary-title">
                  {UI_COPY.validationSummary.title}
                </div>
                <ul className="validation-error-summary-list">
                  {Object.keys(validationErrors).map((key) => (
                    <li key={key}>
                      {FIELD_LABELS[key] ||
                        validationErrors[key]}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="sheet-actions sheet-actions-centered">
              <Button
                variant="outline"
                onClick={handleCancelClick}
                className="sheet-action-button sheet-action-cancel"
                disabled={isSaving}
              >
                {isProfileRequired ? "Sign Out" : BUTTON_LABELS.cancel}
              </Button>
              <Button
                onClick={handleSaveClick}
                className={saveButtonClassName}
                disabled={isSaving}
              >
                {primaryActionButtonLabel}
              </Button>
            </div>

            <div className="profile-footer-text text-xs text-muted">
              {UI_COPY.footer}
            </div>

            {/* Account navigation */}
            <hr className="profile-account-divider" />

            <div className="profile-account-links profile-account-links-centered text-xs">
              {accountLinkItems.map((accountLink, index) => (
                <React.Fragment key={accountLink.href}>
                  <a
                    href={accountLink.href}
                    className="profile-account-link"
                  >
                    {accountLink.label}
                  </a>
                  {index < accountLinkItems.length - 1 && (
                    <span className="profile-account-links-separator">
                      {UI_COPY.accountLinks.separator}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </SheetContent>
      ) : null}
    </Sheet>
  );
}
